
import { mapGetters, mapActions } from 'vuex'
import PdpBreadcrumb from './pdp-breadcrumb'
import PdpGallery from './pdp-gallery'
import PdpBuybox from './pdp-buybox'
import PdpFeatures from './pdp-features'
import PdpPairwith from './pdp-pairswith'
import PdpProductCallouts from './pdp-product-callouts'
import PdpDescription from './pdp-description'
// import PdpIGPhotos from './pdp-igphotos'
import PdpReviews from './pdp-reviews'
import PdpCarouselCategory from './pdp-carousel-category'
import PdpCarouselBottom from './pdp-carousel-bottom'
import RecentlyViewed from '~/components/common/lole-recently-viewed'
import { parseBreadcrumbs } from '~/helpers/shopify/breadcrumbs'
export default {
  components: {
    PdpBreadcrumb,
    PdpGallery,
    PdpBuybox,
    PdpFeatures,
    PdpPairwith,
    PdpProductCallouts,
    PdpDescription,
    // PdpIGPhotos,
    PdpReviews,
    PdpCarouselCategory,
    PdpCarouselBottom,
    RecentlyViewed,
  },
  provide() {
    return {
      product: this.product,
      wording: this.wording,
      // wordingInstagram: this.wordingInstagram,
      wordingCallouts: this.wordingCallouts,
    }
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    wording: {
      type: Object,
      required: true,
    },
    // wordingInstagram: {
    //   type: Object,
    //   required: true,
    // },
    wordingCallouts: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    showZoom: false,
    productId: null,
    selectedColor: null,
    variantId: null,
    variantInWishlist: false,
    verticalScroll: 0,
    reviews: {},
    reviewsResults: [],
    reviewsBreakdown: [],
    reviewsPerPage: 10,
    reviewPage: 1,
    reviewsSortBy: 'date_desc',
    pageBreadcrumbs: [],
  }),
  computed: {
    ...mapGetters({
      vxBreadcrumbs: 'common/getBreadcrumbs',
      vxModelGroups: 'common/getModelGroups',
    }),
    paginatedReviews() {
      const publishedReviewsOnly = this.reviewsResults.filter(
        (result) => result.review.reviewState === 1
      )
      return publishedReviewsOnly.slice(
        0,
        (this.reviewsPerPage + 1) * this.reviewPage
      )
    },
    totalPages() {
      return Math.max(this.reviewsResults.length / this.reviewsPerPage)
    },
  },
  mounted() {
    window.addEventListener('scroll', this.scrollY)
    this.vxAddRecentlyViewedProduct(this.product)
    this.getProductBreakdowns()
    this.getProductReviews()

    // BREADCRUMBS
    if (this.vxBreadcrumbs) {
      this.pageBreadcrumbs = parseBreadcrumbs(
        this.product?.metafield?.breadcrumb,
        this.vxBreadcrumbs,
        this.$i18n.locale
      )
    }
  },
  methods: {
    ...mapActions({
      vxAddRecentlyViewedProduct:
        'recentlyViewedProducts/addRecentlyViewedProduct',
    }),
    async getProductReviews() {
      try {
        const ONE_DAY = 60 * 60 * 24
        const { handlesByGroup } = this.vxModelGroups
        const slug = this.$route.params.slug
        const productHandles = handlesByGroup[slug]
        const productIds = []

        for (const handle of productHandles) {
          const ids = await this.$shopify.product.getAllProductIdsFromHandle(
            handle
          )
          productIds.push(...ids)
        }

        const productReviewsHash = await this.$redisClient.getHashAll(
          `reviews:${slug}`
        )

        let hashKeys = []
        let hashToArray = []
        if (productReviewsHash) {
          hashKeys = Object.keys(productReviewsHash)
          hashToArray = Object.values(productReviewsHash)
        }

        // LOAD EXISTING DATA FROM HASH
        if (hashToArray.length) {
          for (const item of hashToArray) {
            const reviewsResponseData = JSON.parse(item)
            if (reviewsResponseData && reviewsResponseData.results)
              this.reviewsResults.push(...reviewsResponseData.results)
          }
        }

        // FIND MISSING PRODUCT IDS IN HASH
        if (hashKeys.length !== productIds.length) {
          const missingProductIds = productIds.filter((item) => {
            const itemId = item.id.substring(
              item.id.lastIndexOf('/') + 1,
              item.id.length
            )
            if (!hashKeys.includes(itemId)) {
              return itemId
            } else {
              return null
            }
          })

          for (const item of missingProductIds) {
            const itemLocale = `en-${item.locale}`.toLowerCase()
            const itemId = item.id.substring(
              item.id.lastIndexOf('/') + 1,
              item.id.length
            )

            // REVIEWS DATA
            const doReviewsCall = async () => {
              const { data } = await this.$axios.post(
                '/api/stamped/reviews/getReviewsByProduct',
                {
                  productId: itemId,
                  locale: itemLocale,
                  page: this.reviewPage,
                }
              )
              const value = JSON.stringify(data)

              return await this.$redisClient.setHash(
                `reviews:${slug}`,
                itemId,
                value,
                ONE_DAY
              )
            }

            const reviewsCall = await doReviewsCall()
            const reviewsResponseData = JSON.parse(reviewsCall)
            if (reviewsResponseData?.results)
              this.reviewsResults.push(...reviewsResponseData.results)
          }
        }
      } catch (error) {
        this.$sentryException(error, {
          source: 'product page',
          level: 'warning',
          extra: 'STAMPED: failed to get product reviews',
        })
      }
    },
    async getProductBreakdowns() {
      try {
        const ONE_DAY = 60 * 60 * 24
        const { handlesByGroup } = this.vxModelGroups
        const slug = this.$route.params.slug
        const productHandles = handlesByGroup[slug]
        const productIds = []
        const allBreakdowns = []

        for (const handle of productHandles) {
          const ids = await this.$shopify.product.getAllProductIdsFromHandle(
            handle
          )
          productIds.push(...ids)
        }

        const productBreakdownsHash = await this.$redisClient.getHashAll(
          `breakdowns:${slug}`
        )

        let hashKeys = []
        let hashToArray = []
        if (productBreakdownsHash) {
          hashKeys = Object.keys(productBreakdownsHash)
          hashToArray = Object.values(productBreakdownsHash)
        }

        // LOAD EXISTING DATA FROM HASH
        if (hashToArray.length) {
          for (const item of hashToArray) {
            const hashedData = JSON.parse(item)
            if (hashedData && hashedData.length)
              allBreakdowns.push(hashedData[0])
          }
        }

        // FIND MISSING PRODUCT IDS IN HASH
        if (hashKeys.length !== productIds.length) {
          const missingProductIds = productIds.filter((item) => {
            const itemId = item.id.substring(
              item.id.lastIndexOf('/') + 1,
              item.id.length
            )
            if (!hashKeys.includes(itemId)) {
              return itemId
            } else {
              return null
            }
          })

          for (const item of missingProductIds) {
            const itemLocale = `en-${item.locale}`.toLowerCase()
            const itemId = item.id.substring(
              item.id.lastIndexOf('/') + 1,
              item.id.length
            )

            // REVIEWS DATA
            const doBreakdownsCall = async () => {
              const { data } = await this.$axios.post(
                '/api/stamped/reviews/getProductReviewBreakdown',
                {
                  productId: itemId,
                  locale: itemLocale,
                }
              )

              const value = JSON.stringify([
                {
                  productId: data[0].productId,
                  rating: data[0].rating,
                  count: data[0].count,
                  breakdown: data[0].breakdown,
                },
              ])

              return await this.$redisClient.setHash(
                `breakdowns:${slug}`,
                itemId,
                value,
                ONE_DAY
              )
            }

            const breakdownsCall = await doBreakdownsCall()
            const responseData = JSON.parse(breakdownsCall)
            if (responseData?.length) allBreakdowns.push(responseData[0])
          }
        }

        this.reviewsBreakdown = allBreakdowns
      } catch (error) {
        this.$sentryException(error, {
          source: 'product page',
          level: 'warning',
          extra: 'STAMPED: failed to get product breakdowns',
        })
      }
    },
    sortReviewsBy(sortBy) {
      this.reviewsSortBy = sortBy
    },
    loadNextReviewPage() {
      this.reviewPage++
    },
  },
}
